// Finite decimal arithmetic. No precision cap, quantization or rounding is applied.
// Conversion back to the existing numeric API is allowed only when its decimal
// spelling is unchanged; callers can then reject an unsupported result explicitly.
export class ExactDecimal {
  readonly units: bigint;
  readonly scale: number;
  constructor(units: bigint, scale = 0) {
    while (scale > 0 && units % 10n === 0n) { units /= 10n; scale--; }
    this.units = units; this.scale = units === 0n ? 0 : scale;
  }
  static from(value: number | string | bigint): ExactDecimal {
    if (typeof value === 'number' && !Number.isFinite(value)) throw new RangeError('Invalid decimal');
    const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(String(value).trim());
    if (!match) throw new RangeError('Invalid decimal');
    const exponent = Number(match[4] || 0);
    if (!Number.isInteger(exponent) || Math.abs(exponent) > 324 || match[2].length + (match[3]?.length || 0) > 1000) throw new RangeError('Decimal exceeds supported range');
    let scale = (match[3]?.length || 0) - exponent;
    let units = BigInt((match[1] === '-' ? '-' : '') + match[2] + (match[3] || ''));
    if (scale < 0) { units *= 10n ** BigInt(-scale); scale = 0; }
    return new ExactDecimal(units, scale);
  }
  add(other: ExactDecimal) {
    const scale = Math.max(this.scale, other.scale);
    return new ExactDecimal(this.units * 10n ** BigInt(scale - this.scale) + other.units * 10n ** BigInt(scale - other.scale), scale);
  }
  subtract(other: ExactDecimal) { return this.add(new ExactDecimal(-other.units, other.scale)); }
  multiply(other: ExactDecimal) { return new ExactDecimal(this.units * other.units, this.scale + other.scale); }
  compare(other: ExactDecimal) { const units = this.subtract(other).units; return units < 0n ? -1 : units > 0n ? 1 : 0; }
  divideTruncated(other: ExactDecimal, places = 2): ExactDecimal | null {
    if (other.units === 0n) return null;
    const numerator = this.units * 10n ** BigInt(other.scale + places);
    const denominator = other.units * 10n ** BigInt(this.scale);
    // BigInt division discards the fractional remainder toward zero, never rounds up.
    return new ExactDecimal(numerator / denominator, places);
  }
  divideExact(other: ExactDecimal): ExactDecimal | null {
    if (other.units === 0n) return null;
    let numerator = this.units * 10n ** BigInt(other.scale), denominator = other.units * 10n ** BigInt(this.scale);
    if (denominator < 0n) { numerator = -numerator; denominator = -denominator; }
    let a = numerator < 0n ? -numerator : numerator, b = denominator;
    while (b) { const rest = a % b; a = b; b = rest; }
    numerator /= a; denominator /= a;
    let rest = denominator, twos = 0, fives = 0;
    while (rest % 2n === 0n) { rest /= 2n; twos++; }
    while (rest % 5n === 0n) { rest /= 5n; fives++; }
    if (rest !== 1n) return null; // Recurring decimal: do not invent a rounded amount.
    const scale = Math.max(twos, fives);
    return new ExactDecimal(numerator * 10n ** BigInt(scale) / denominator, scale);
  }
  toString() {
    const negative = this.units < 0n, text = String(negative ? -this.units : this.units).padStart(this.scale + 1, '0');
    return (negative ? '-' : '') + (this.scale ? text.slice(0, -this.scale) + '.' + text.slice(-this.scale) : text);
  }
  toNumberExact() {
    const value = Number(this.toString());
    if (!Number.isFinite(value) || ExactDecimal.from(value).compare(this) !== 0) throw new RangeError('Decimal cannot be represented by the numeric API without changing its value');
    return value;
  }
}

export function storageUnits(value: number | string | bigint): bigint | null {
  try {
    const decimal = ExactDecimal.from(value);
    if (decimal.scale > 18) return null;
    const units = decimal.units * 10n ** BigInt(18 - decimal.scale);
    return (units < 0n ? -units : units) < 10n ** 31n ? units : null;
  } catch { return null; }
}
export function isStorageDecimal(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (typeof value !== 'number' && typeof value !== 'string') return false;
  try { return storageUnits(value) !== null && Number.isFinite(ExactDecimal.from(value).toNumberExact()); }
  catch { return false; }
}
